import React, { useState, useEffect, useRef } from "react";
import type { Lesson } from "@/data/lessons";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

interface CodeReviewProps {
  lesson: Lesson;
  wpm: number;
  accuracy: number;
  onContinue: () => void;
}

export default function CodeReview({ lesson, wpm, accuracy, onContinue }: CodeReviewProps) {
  const [secondsLeft, setSecondsLeft] = useState(600); // 10 minutes
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;

  return (
    <div className="min-h-screen p-4 max-w-4xl mx-auto flex flex-col gap-6">
      <div className="text-center">
        <h1 className="text-2xl font-bold mb-1">Lesson {lesson.id} Complete!</h1>
        <p className="text-muted-foreground">
          {wpm} WPM / {accuracy}% accuracy
        </p>
        <p className="text-sm text-muted-foreground mt-2">
          Review time: {minutes}:{seconds.toString().padStart(2, "0")} remaining
        </p>
      </div>

      <p className="text-center text-muted-foreground text-sm">
        Take some time to read through the code you typed and understand what it does.
      </p>

      <div className="flex flex-col gap-4">
        {lesson.snippets.map((snippet) => (
          <Card key={snippet.id}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                {snippet.title}
                {snippet.language !== "text" && (
                  <span className="ml-2 text-xs text-muted-foreground font-normal">
                    {snippet.language}
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <pre className="bg-muted rounded-lg p-3 font-mono text-sm whitespace-pre-wrap overflow-x-auto">
                {snippet.code}
              </pre>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {snippet.explanation}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="text-center py-4">
        <Button size="lg" onClick={onContinue}>
          {secondsLeft > 0 ? "Skip Review & Continue" : "Continue to Next Lesson"}
        </Button>
      </div>
    </div>
  );
}
